import { createContext, ReactNode, useContext, useState } from "react";

export type CompletableDeviceId = "burrito-racer" | "memory-patch" | "vhs-fix" | "robot-or-not";

// powerOnDevice is also used for "reveal-code", which isn't a completable
// mini-game device, just a powered state used to reveal the room code.
type PoweredDeviceId = CompletableDeviceId | "reveal-code";

type GremlinVariant = "normal" | "evil" | "burrito" | "shades";

type GremlinState = {
  decisionMade: boolean;
  isEvil: boolean;
  variant: GremlinVariant;
};

type CompletedDevices = Record<CompletableDeviceId, boolean>;

type Room8ContextValue = {
  poweredDevice: PoweredDeviceId | null;
  completedDevices: CompletedDevices;
  areMainGamesComplete: boolean;
  gremlinState: GremlinState;
  powerOnDevice: (deviceId: PoweredDeviceId) => void;
  powerOffAllDevices: () => void;
  markDeviceComplete: (deviceId: CompletableDeviceId) => void;
  leaveGremlinAlone: () => void;
  pourWaterOnGremlin: () => void;
  feedGremlinBurrito: () => void;
  showGremlinOutside: () => void;
};

const initialGremlinState: GremlinState = {
  decisionMade: false,
  isEvil: false,
  variant: "normal"
};

const Room8Context = createContext<Room8ContextValue>({
  poweredDevice: null,
  completedDevices: {
    "burrito-racer": false,
    "memory-patch": false,
    "vhs-fix": false,
    "robot-or-not": false
  },
  areMainGamesComplete: false,
  gremlinState: initialGremlinState,
  powerOnDevice: () => {},
  powerOffAllDevices: () => {},
  markDeviceComplete: () => {},
  leaveGremlinAlone: () => {},
  pourWaterOnGremlin: () => {},
  feedGremlinBurrito: () => {},
  showGremlinOutside: () => {}
});

const initialCompletionState: CompletedDevices = {
  "burrito-racer": false,
  "memory-patch": false,
  "vhs-fix": false,
  "robot-or-not": false
};

export const Room8Provider = ({ children }: { children: ReactNode }) => {
  const [poweredDevice, setPoweredDevice] = useState<PoweredDeviceId | null>(null);
  const [completedDevices, setCompletedDevices] = useState<CompletedDevices>(initialCompletionState);
  const [gremlinState, setGremlinState] = useState<GremlinState>(initialGremlinState);

  const powerOnDevice = (deviceId: PoweredDeviceId) => {
    setPoweredDevice(deviceId);
  };

  const powerOffAllDevices = () => {
    setPoweredDevice(null);
  };

  const markDeviceComplete = (deviceId: CompletableDeviceId) => {
    setCompletedDevices((prev) => ({
      ...prev,
      [deviceId]: true
    }));
  };

  const leaveGremlinAlone = () => {
    setGremlinState({
      decisionMade: true,
      isEvil: false,
      variant: "normal"
    });
  };

  const pourWaterOnGremlin = () => {
    setGremlinState({
      decisionMade: true,
      isEvil: true,
      variant: "evil"
    });
  };

  const feedGremlinBurrito = () => {
    setGremlinState({
      decisionMade: true,
      isEvil: false,
      variant: "burrito"
    });
  };

  const showGremlinOutside = () => {
    setGremlinState({
      decisionMade: true,
      isEvil: false,
      variant: "shades"
    });
  };

  const areMainGamesComplete = Object.values(completedDevices).every(Boolean);

  return (
    <Room8Context.Provider
      value={{
        poweredDevice,
        completedDevices,
        areMainGamesComplete,
        gremlinState,
        powerOnDevice,
        powerOffAllDevices,
        markDeviceComplete,
        leaveGremlinAlone,
        pourWaterOnGremlin,
        feedGremlinBurrito,
        showGremlinOutside
      }}
    >
      {children}
    </Room8Context.Provider>
  );
};

export const useRoom8 = () => useContext(Room8Context);